import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useClinics } from "../../hooks/useClinics";
import {
  ClinicsClient,
  FileParameter,
  UpdateClinicInfoCommand,
} from "../../web-api-client";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { useToast } from "../../hooks/use-toast";
import { makeFetchWithAuth } from "../../auth/authFetch";
import { useNavigate } from "react-router-dom";
import LoadingIndicator from "../../components/LoadingIndicator";
import { useStickyLoading } from "../../hooks/useStickyLoading";
import { useQueryClient } from "@tanstack/react-query";
import { toUserMessage } from "../../utils/toUserMessage";
import { ClinicList } from "../../components/admin/clinic/ClinicList";
import { appConfig } from "../../appconfig";

function ClinicManager() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updatingRef = useRef(false);

  const {
    data: clinics = [],
    isLoading: isClinicFetching,
    refetch,
  } = useClinics(false);

  const isBusy = isLoading || isClinicFetching;

  const overlayVisible = useStickyLoading(isBusy, {
    showDelay: 150,
    minVisible: 300,
  });

  const createClient = () =>
    new ClinicsClient(appConfig.apiBaseUrl, {
      fetch: makeFetchWithAuth(fetchWithTimeout(), {
        onUnauthorized: () =>
          navigate("/login", {
            replace: true,
            state: { from: window.location.pathname },
          }),
      }),
    });

  const updateClinic = async (
    id: number,
    payload: {
      file: File | null;
      data: ClinicForm | null;
      deleteCurrentImage: boolean;
    }
  ): Promise<void> => {
    if (isLoading || updatingRef.current) return;

    updatingRef.current = true;
    setIsLoading(true);

    const client = createClient();

    try {
      const { file, data, deleteCurrentImage } = payload;

      // 1) cập nhật thông tin text
      if (data) {
        const cmd = new UpdateClinicInfoCommand();
        cmd.description = data.description ?? "";
        await client.postApiClinics(id, cmd);
      }

      // 2) nếu có file mới => upload file mới
      if (file) {
        const fp: FileParameter = {
          data: file,
          fileName: file.name,
        };
        await client.postApiClinicsImage(String(id), fp);
      }
      // 3) nếu không có file mới nhưng user muốn xóa ảnh hiện tại => gọi DELETE
      else if (deleteCurrentImage) {
        await client.deleteApiClinicsImage(String(id));
      }

      await queryClient.invalidateQueries({ queryKey: ["clinics"] });

      toast({
        variant: "success",
        title: "Cập nhật phòng khám thành công",
        duration: 3000,
      });
    } catch (error: any) {
      const errMsg = await toUserMessage(error);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: errMsg,
        duration: 3000,
      });
    } finally {
      updatingRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Quản lý phòng khám";
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {overlayVisible && (
        <div className="fixed inset-0 z-50 touch-none overscroll-contain">
          <div className="absolute inset-0 bg-white/50" aria-hidden="true" />
          <div className="relative flex h-full w-full items-center justify-center">
            <LoadingIndicator />
          </div>
        </div>
      )}

      <main className="w-full ">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-white px-5 py-3 md:px-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center ">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-slate-900">
                  Danh sách phòng khám
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Làm mới
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-6">
            <ClinicList clinics={clinics} onClinicUpdate={updateClinic} />
          </div>
        </div>
      </main>
    </div>
  );
}

export default ClinicManager;